import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import GeneralPageLayout from '../../../../components/layout/GeneralPageLayout';
import usePlaceNavigate from '../../../../hooks/usePlaceNavigate';
import { useCloseWindow } from '../../../../hooks/useCloseWindow';
import { buildPaymentApi } from '../../../../api';
import { resolvePaymentErrorMessage } from '../../../../api/payment/paymentErrors';
import { selectPciPaymentApiEnabled } from '../../../../store/slices/config/configSelectors';
import { selectPayment } from '../../../../store/slices/payment/paymentSelectors';
import { PostbackStatus } from '../../../../types/payment';
import { GENERIC_PAYMENT_ERROR } from '../../../../components/payment/constants';

interface SnapPayFormData {
  interopReqNo: string;
  interopJSURL: string;
  interopMainURL: string;
}

const SNAPPAY_IFRAME_ELEMENT_IDS = [
  'interop_form',
  'interopform_overlay',
  'interopform_iframe',
  'receipt_overlay',
  'receipt_iframe',
];

function SnapPay() {
  const placeNavigate = usePlaceNavigate();
  const { search } = useLocation();
  const params = useMemo(() => new URLSearchParams(search), [search]);
  const paymentId = params.get('paymentId') as string;
  const payment = useSelector(selectPayment);
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const api = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const { isAbleToCloseWindow, closeWindow } = useCloseWindow();

  // RedirectBasedPayment dispatches payGeneric's formData into the payment
  // slice right before navigating here.
  const formData = payment.formData as unknown as SnapPayFormData | undefined;

  const cleanup = useCallback(() => {
    SNAPPAY_IFRAME_ELEMENT_IDS.forEach((id) => {
      document.getElementById(id)?.remove();
    });
  }, []);

  useEffect(() => cleanup(), [cleanup]);

  const errorInData = useMemo(() => {
    if (
      !paymentId ||
      !formData?.interopReqNo ||
      !formData?.interopJSURL ||
      !formData?.interopMainURL
    ) {
      return 'Not enough information to process the payment.';
    }
    return null;
  }, [paymentId, formData]);

  const fiservOrigin = useMemo(() => {
    if (!formData?.interopMainURL) {
      return undefined;
    }
    try {
      return new URL(formData.interopMainURL).origin;
    } catch {
      return undefined;
    }
  }, [formData]);

  useEffect(() => {
    if (!formData) {
      return;
    }
    setLoading(true);
    setError(undefined);

    const messageHandler = async (event: MessageEvent) => {
      // The FiServ overlay can run as a popup with window.opener set, so this
      // window may also receive unrelated postMessage traffic (auth-token
      // sync, dev-tools, etc.) — including on initial load. Only react to
      // messages that actually come from the FiServ overlay's own origin.
      if (!fiservOrigin || event.origin !== fiservOrigin) {
        return;
      }

      if (event.data?.hppurl) {
        // User closed the FiServ overlay — treat it as a failed payment.
        cleanup();
        setLoading(false);
        placeNavigate('/payment-error');
        return;
      }

      // FiServ overlay has fired — post the token now so GetPaymentDetails returns the real result.
      window.removeEventListener('message', messageHandler);

      try {
        const postbackResult = await api.snapPayPostback(
          paymentId,
          formData.interopReqNo
        );
        cleanup();
        if (
          postbackResult.status === PostbackStatus.COMPLETE ||
          postbackResult.status === PostbackStatus.PENDING
        ) {
          placeNavigate('/payment-completed');
        } else {
          placeNavigate('/payment-error');
        }
      } catch (err) {
        cleanup();
        setError(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
        setLoading(false);
      }
    };

    window.addEventListener('message', messageHandler);

    const script = document.createElement('script');
    script.id = 'interop_form';
    script.src = formData.interopJSURL;
    script.dataset.requestid = formData.interopReqNo;
    script.dataset.interopurl = formData.interopMainURL;
    document.body.appendChild(script);
  }, [formData, fiservOrigin, paymentId, api, placeNavigate, cleanup]);

  if (errorInData || error) {
    return (
      <GeneralPageLayout
        showNext={isAbleToCloseWindow}
        next="Close Window"
        onNext={closeWindow}
      >
        {errorInData || error}
      </GeneralPageLayout>
    );
  }

  return (
    <GeneralPageLayout>
      <div className="payment-container">{loading && <p>Loading...</p>}</div>
    </GeneralPageLayout>
  );
}

export default SnapPay;
